/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ActionIcon, Box, Breadcrumbs, Flex, Group, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { GraphQLQuery, generateClient } from "aws-amplify/api";
import ConfirmDeleteCaseModal from "@/components/Case/ConfirmDeleteCaseModal";
import TemplateFormModal from "@/components/Template/TemplateForm/TemplateFormModal";
import TemplateContent from "@/components/TemplateDetail/TemplateContent";
import AddFieldTemplateModal from "@/components/TemplateDetail/TemplateModals/AddFieldTemplateModal";
import AddSectionTemplateModal from "@/components/TemplateDetail/TemplateModals/AddSectionTemplateModal";
import AddSubSectionTemplateModal from "@/components/TemplateDetail/TemplateModals/AddSubSectionTemplateModal";
import AddTextBoxTemplateModal from "@/components/TemplateDetail/TemplateModals/AddTextBoxTemplateModal";
import SelectOptionAddToTemplateModal from "@/components/TemplateDetail/TemplateModals/SelectOptionAddToTemplateModal";
import { TEMPLATE_OPTIONS } from "@/utils/utils";
import { getTemplateShallow } from "@/graphql/getTemplateShallow";
import { GetTemplateQuery, GetTemplateQueryVariables } from "@/API";

const client = generateClient();

const TemplateDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const templateState = location.state;
  if (templateState == null) {
    window.location.href = "/templates";
  }
  const [template, setTemplate] = useState<any>();
  const [isEmptySection, setIsEmptySection] = useState(true);
  const [modalEditOpened, setModalEditOpened] = useState(false);
  const [modalAddSectionOpened, setModalAddSectionOpened] = useState(false);
  const [modalAddSubSectionOpened, setModalAddSubSectionOpened] =
    useState(false);
  const [modalAddFieldOpened, setModalAddFieldOpened] = useState(false);
  const [modalAddTextBoxOpened, setModalAddTextBoxOpened] = useState(false);
  const [modalAddStandardSectionOpened, setModalAddStandardSectionOpened] =
    useState(false);
  const [modalConfirmDeleteOpened, setModalConfirmDeleteOpened] =
    useState(false);

  useEffect(() => {
    if (!templateState?.id) {
      return navigate("/templates");
    }
    fetchTemplate(templateState.id);
  }, [templateState]);

  useEffect(() => {
    checkEmptySection();
  }, [template]);

  const items = [
    { title: "Templates", href: "/templates" },
    { title: "Build template", href: "", isActive: true },
  ].map((item, index) => (
    <NavLink to={item.href} key={index}>
      {item.title}
    </NavLink>
  ));

  const checkEmptySection = () => {
    const sections = template?.sections;
    if (sections?.items.length === 0) {
      setIsEmptySection(true);
    } else {
      setIsEmptySection(false);
    }
  };

  const fetchTemplate = async (templateId: string) => {
    const templateQueryInput: GetTemplateQueryVariables = {
      id: templateId,
    };
    const templateData = await client.graphql<GraphQLQuery<GetTemplateQuery>>({
      query: getTemplateShallow,
      variables: templateQueryInput,
    });
    if (templateData.data) {
      setTemplate(templateData.data.getTemplate);
      
      // Trigger template migration to Supabase when viewing individual template
      const fetchedTemplate = templateData.data.getTemplate;
      if (fetchedTemplate?.id) {
        setTimeout(async () => {
          try {
            const { DatabaseMigrationAdapter } = await import("@/services/migration");
            const migrationAdapter = DatabaseMigrationAdapter.getInstance();
            
            console.log('[TEMPLATE-MIGRATION] Migrating template:', fetchedTemplate.name);
            await migrationAdapter.handleTemplateOperation('CREATE', fetchedTemplate as Record<string, unknown>, { data: { createTemplate: fetchedTemplate } } as Record<string, unknown>);
            console.log('[TEMPLATE-MIGRATION] Successfully migrated template to Supabase');
          } catch (error) {
            console.warn('[TEMPLATE-MIGRATION] Failed to migrate template:', error);
          }
        }, 1000); // Start after 1 second
      }
    }
  };

  const onSelectedOption = (option: string) => {
    switch (option) {
      case TEMPLATE_OPTIONS.SECTION:
        setModalAddSectionOpened(true);
        break;
      case TEMPLATE_OPTIONS.SUB_SECTION:
        setModalAddSubSectionOpened(true);
        break;
      case TEMPLATE_OPTIONS.FIELD:
        setModalAddFieldOpened(true);
        break;
      case TEMPLATE_OPTIONS.TEXT_BOX:
        setModalAddTextBoxOpened(true);
        break;
    }
  };

  return (
    <Box p={15}>
      <Breadcrumbs separatorMargin={3} mt={0} separator="→">
        {items}
      </Breadcrumbs>
      <Group
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex",
          width: "100%",
          maxHeight: "100%",
        }}
      >
        <Group
          className="header-patients"
          flex={1}
          mt={10}
          align="center"
          w={"100%"}
          justify="space-between"
        >
          <Flex align={"center"}>
            {templateState && (
              <>
                <Flex direction={"column"}>
                  <Text fz={20} fw={"bold"} mr={5} lh={1}>
                    {templateState?.name || ""}
                  </Text>
                </Flex>
                <ActionIcon
                  radius={20}
                  size={"md"}
                  p={3}
                  onClick={() => {
                    if (template?.advanced) {
                      setModalEditOpened(true);
                    } else {
                      setModalAddStandardSectionOpened(true);
                    }
                  }}
                >
                  <IconPlus size={"lg"} />
                </ActionIcon>
              </>
            )}
          </Flex>
        </Group>
        <Flex
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "start",
            alignItems: "center",
            position: "relative",
            width: "100%",
            minHeight: "calc(100vh - 300px)",
          }}
        >
          {isEmptySection ? (
            <Text>Tap + to get started!</Text>
          ) : (
            <TemplateContent
              template={template}
              fetchTemplate={fetchTemplate}
            />
          )}
        </Flex>
        <SelectOptionAddToTemplateModal
          modalOpened={modalEditOpened}
          setModalOpened={setModalEditOpened}
          onSelectedOption={onSelectedOption}
          template={template}
        />
        <AddSectionTemplateModal
          fetchTemplate={fetchTemplate}
          modalOpened={modalAddSectionOpened}
          setModalOpened={setModalAddSectionOpened}
          template={template}
        />
        <AddSubSectionTemplateModal
          fetchTemplate={fetchTemplate}
          modalOpened={modalAddSubSectionOpened}
          setModalOpened={setModalAddSubSectionOpened}
          template={template}
        />
        <AddFieldTemplateModal
          fetchTemplate={fetchTemplate}
          modalOpened={modalAddFieldOpened}
          setModalOpened={setModalAddFieldOpened}
          template={template}
        />
        <AddTextBoxTemplateModal
          fetchTemplate={fetchTemplate}
          modalOpened={modalAddTextBoxOpened}
          setModalOpened={setModalAddTextBoxOpened}
          template={template}
        />
        <ConfirmDeleteCaseModal
          modalOpened={modalConfirmDeleteOpened}
          setModalOpened={setModalConfirmDeleteOpened}
          patientCase={template}
          fetchCases={() => {}}
        />
        <TemplateFormModal
          opened={modalAddStandardSectionOpened}
          onClose={() => setModalAddStandardSectionOpened(false)}
          selectedTemplate={template}
          fetchTemplate={() => fetchTemplate(template?.id)}
        />
      </Group>
    </Box>
  );
};

export default TemplateDetail;
